// Per-task incremental sync with retry queue and multi-tab awareness.
// Falls back to full PUT for categories/settings/journal (small structures).
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
export const api = axios.create({ baseURL: API, withCredentials: true });

const QUEUE_KEY = "tl:sync-queue";
const CHANNEL = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("tl:sync") : null;

function loadQueue() {
  try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]"); } catch { return []; }
}
function saveQueue(q) {
  try { localStorage.setItem(QUEUE_KEY, JSON.stringify(q)); } catch {}
}

let processing = null; // holds current in-flight drain promise
let retryTimer = null;
let onRemoteChange = null;
let lastError = false;
let unreachable = false; // network error / server down (not auth errors)
export function setServerReachable(ok) {
  if (unreachable === !ok) return;
  unreachable = !ok;
  emitState();
}
function isUnreachableError(err) {
  const st = err?.response?.status;
  return !st || st >= 500;
}

function emitState() {
  try {
    const q = loadQueue();
    const detail = { pending: q.length, syncing: !!processing, error: lastError, unreachable };
    window.dispatchEvent(new CustomEvent("tl:sync-state", { detail }));
  } catch {}
}

async function runOne(op) {
  if (op.type === "task_upsert") {
    await api.put(`/planner/tasks/${encodeURIComponent(op.id)}`, { task: op.task });
  } else if (op.type === "task_delete") {
    await api.delete(`/planner/tasks/${encodeURIComponent(op.id)}`);
  } else if (op.type === "category_delete") {
    try {
      await api.delete(`/planner/categories/${encodeURIComponent(op.id)}`);
    } catch (err) {
      if (err?.response?.status !== 400) throw err;
    }
  } else if (op.type === "meta") {
    // Full planner sync for meta (settings/categories/journal). Debounced upstream.
    const { data } = await api.put(`/planner`, { data: op.data });
    if (data?.categories && onCategoriesMerged) onCategoriesMerged(data.categories);
  }
}

let onCategoriesMerged = null;
export function setCategoriesMergedHandler(fn) { onCategoriesMerged = fn; }

export function enqueueCategoryDelete(id) {
  if (!id) return;
  const q = loadQueue();
  q.push({ type: "category_delete", id, ts: Date.now() });
  saveQueue(q);
  emitState();
  drainQueue();
}

async function drainQueue() {
  if (processing) return processing;
  const run = (async () => {
    let op = loadQueue()[0];
    if (!op) { lastError = false; emitState(); return; }
    emitState();
    while (op) {
      try {
        await runOne(op);
        // Re-read the queue: ops may have been added (or coalesced) while this request was in flight
        const fresh = loadQueue();
        const key = JSON.stringify(op);
        const idx = fresh.findIndex((o) => JSON.stringify(o) === key);
        if (idx >= 0) fresh.splice(idx, 1);
        saveQueue(fresh);
        lastError = false;
        unreachable = false;
        emitState();
        // Notify other tabs a change was persisted
        if (CHANNEL) CHANNEL.postMessage({ kind: "remote-change", op });
        op = fresh[0];
      } catch (err) {
        console.error("sync op failed, will retry:", op.type, err?.response?.status || err?.message);
        lastError = true;
        unreachable = isUnreachableError(err);
        emitState();
        // Backoff then break; scheduleRetry will re-run
        scheduleRetry(true);
        break;
      }
    }
  })();
  processing = run.finally(() => { processing = null; emitState(); });
  return processing;
}

let backoffMs = 3000;
function scheduleRetry(fromFailure = false) {
  if (retryTimer) clearTimeout(retryTimer);
  if (fromFailure) {
    backoffMs = Math.min(60000, backoffMs * 2);
  } else {
    backoffMs = 3000;
  }
  retryTimer = setTimeout(() => { drainQueue(); }, backoffMs);
}

export function enqueueTaskUpsert(task) {
  if (!task || !task.id) return;
  const q = loadQueue();
  // Coalesce: remove any earlier upsert or pending delete for the same id
  const filtered = q.filter((o) => !((o.type === "task_upsert" || o.type === "task_delete") && o.id === task.id));
  filtered.push({ type: "task_upsert", id: task.id, task, ts: Date.now() });
  saveQueue(filtered);
  emitState();
  drainQueue();
}

export function enqueueTaskDelete(id) {
  if (!id) return;
  const q = loadQueue();
  // Remove any earlier upsert for the same id
  const filtered = q.filter((o) => !((o.type === "task_upsert") && o.id === id));
  filtered.push({ type: "task_delete", id, ts: Date.now() });
  saveQueue(filtered);
  emitState();
  drainQueue();
}

let metaDebounce = null;
let pendingMeta = null;
export function enqueueMeta(data) {
  // Debounce meta (settings/categories/journal + full tasks snapshot as safety net)
  if (metaDebounce) clearTimeout(metaDebounce);
  pendingMeta = data;
  metaDebounce = setTimeout(flushPendingMeta, 600);
}
function flushPendingMeta() {
  if (metaDebounce) clearTimeout(metaDebounce);
  metaDebounce = null;
  if (!pendingMeta) return;
  const q = loadQueue().filter((o) => o.type !== "meta");
  q.push({ type: "meta", data: pendingMeta, ts: Date.now() });
  pendingMeta = null;
  saveQueue(q);
  emitState();
  drainQueue();
}

// Flush queue on visibility change / before unload
if (typeof window !== "undefined") {
  window.addEventListener("online", () => drainQueue());
  window.addEventListener("focus", () => drainQueue());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") drainQueue();
    else flushPendingMeta();
  });
  let beaconSent = false;
  window.addEventListener("pageshow", () => { beaconSent = false; });
  const sendAllViaBeacon = () => {
    if (beaconSent) return;
    if (metaDebounce) flushPendingMeta();
    const q = loadQueue();
    if (!q.length) return;
    beaconSent = true;
    // Best-effort sync send via sendBeacon for ALL pending operations
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      if (!navigator.sendBeacon) return;
      for (const op of q) {
        if (op.type === "task_upsert") {
          const blob = new Blob([JSON.stringify({ task: op.task })], { type: "application/json" });
          navigator.sendBeacon(`${backendUrl}/api/planner/tasks/${encodeURIComponent(op.id)}/beacon-upsert`, blob);
        } else if (op.type === "task_delete") {
          // Beacon does not support DELETE verb; use the beacon-delete endpoint (POST body -> delete)
          const blob = new Blob([JSON.stringify({ id: op.id })], { type: "application/json" });
          navigator.sendBeacon(`${backendUrl}/api/planner/tasks/${encodeURIComponent(op.id)}/beacon-delete`, blob);
        } else if (op.type === "category_delete") {
          const blob = new Blob([JSON.stringify({})], { type: "application/json" });
          navigator.sendBeacon(`${backendUrl}/api/planner/categories/${encodeURIComponent(op.id)}/beacon-delete`, blob);
        } else if (op.type === "meta") {
          const blob = new Blob([JSON.stringify({ data: op.data })], { type: "application/json" });
          navigator.sendBeacon(`${backendUrl}/api/planner/beacon`, blob);
        }
      }
    } catch {}
  };
  window.addEventListener("beforeunload", sendAllViaBeacon);
  window.addEventListener("pagehide", sendAllViaBeacon);
}

if (CHANNEL) {
  CHANNEL.onmessage = (evt) => {
    if (evt.data?.kind === "remote-change" && onRemoteChange) onRemoteChange();
  };
}

export function setRemoteChangeHandler(fn) { onRemoteChange = fn; }
export function drainNow() { return drainQueue(); }
export function pendingCount() { return loadQueue().length; }
export function getPendingOps() { return loadQueue(); }
export function getSyncState() {
  return { pending: loadQueue().length, syncing: !!processing, error: lastError, unreachable };
}
